import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { mapToRequestData } from '@agco-onedigital/ecosystem-lib-frontend-utils';

import { AsyncThunkConfig, RootState } from '../../../store';

import { IConsents, IRegisterAgcoUserResponse } from './types';
import {
  AUTHORIZAZION_FORM_CONSENTS,
  EMPTY_ADDRESS,
  getPhone,
  mapAddressData,
  mapSubscriptions,
  toContactPointTypeConsents,
} from './utils';

const SELF_REGISTRATION_PATH = '/api/customers/applications/self-registration';
type RegistrationData = RootState['registration']['data'];

const getRegistrationHeaders = (registrationData: RegistrationData, consumerApp: string) => ({
  'x-consumer-app': consumerApp,
  'x-correlation-id': registrationData.otpTransactionId ?? '',
  ...(registrationData.activationCodeState?.activationCode && {
    'x-ciob-access-code': registrationData.activationCodeState.activationCode,
  }),
});

const getMarketingConsent = (registrationData: RegistrationData) => {
  const { marketingConsentLevel } = registrationData;

  return marketingConsentLevel && marketingConsentLevel !== 'NOT_SET'
    ? { marketingConsent: marketingConsentLevel }
    : {};
};

const createMarketingRegistrationPayload = (registrationData: RegistrationData) => {
  const storedSubscriptions = registrationData.subscriptions;
  const subscriptions = mapSubscriptions(storedSubscriptions);
  const hasSubscriptions = subscriptions.length > 0;
  const { account, details } = registrationData;

  return {
    individual: {
      salutation: details?.salutation ?? undefined,
      firstName: details?.firstName ?? '',
      lastName: details?.lastName ?? '',
      locale: registrationData.locale ?? '',
      ...getMarketingConsent(registrationData),
      communicationSubscriptionConsents: subscriptions,
      contactPointTypeConsents: hasSubscriptions ? toContactPointTypeConsents(storedSubscriptions || []) : [],
      authorizationFormConsents: hasSubscriptions ? AUTHORIZAZION_FORM_CONSENTS : [],
    },
    contactEmail: [
      {
        emailAddress: registrationData.email,
        contactPointType: 'ContactPointEmail',
        primaryFlag: true,
        activeFromDate: new Date().toISOString().slice(0, 10),
      },
    ],
    businessPhone: getPhone(registrationData.phoneNumbers?.office),
    mobilePhone: getPhone(registrationData.phoneNumbers?.mobile),
    mailingAddress: [mapAddressData(registrationData.address?.addressLine1 ? registrationData.address : EMPTY_ADDRESS)],
    account: {
      ...account,
      shippingAddress: account?.shippingAddress?.map(mapAddressData),
      billingAddress: account?.billingAddress?.map(mapAddressData),
    },
    identity: {
      password: registrationData.password,
    },
    accountContactRoles: [
      {
        isDirect: true,
        roles: ['Owner'],
      },
    ],
  };
};

const createLegacyRegistrationPayload = (registrationData: RegistrationData, consents: IConsents) =>
  mapToRequestData(
    {
      data: {
        ...registrationData,
        address: {
          addressLine1: '',
          city: '',
          postalCode: '',
          country: '',
          ...(registrationData.address || undefined),
        },
      },
    },
    consents,
    registrationData.locale,
  );

export const registerAgcoUser = createAsyncThunk<void, IConsents, AsyncThunkConfig>(
  'registration/registerAgcoUser',
  async (consents, { getState }) => {
    const { launchDarkly, registration } = getState();
    const isMarketingPrefEnabled = launchDarkly.data['marketing-preferences-in-registration-enabled'] ?? false;
    const mule = launchDarkly.data['config-web-registration']?.integration?.mule?.applications?.['mule-ep-customers'];
    const muleBaseUrl = mule?.baseUrl ?? '';
    const isUserFromFendtOne = sessionStorage.getItem('redirect') === 'true';
    const ptxHeaderEnabled = launchDarkly.data['ptx-registration-header-enabled'] && isUserFromFendtOne;
    const consumerApp = ptxHeaderEnabled ? 'PTX' : (mule?.constants?.['consumer-app'] ?? '');
    if (!muleBaseUrl) {
      throw new Error('Mule base url is missing in launch darkly config!');
    }

    const registrationPayload = isMarketingPrefEnabled
      ? createMarketingRegistrationPayload(registration.data)
      : createLegacyRegistrationPayload(registration.data, consents);

    await axios.post<IRegisterAgcoUserResponse>(`${muleBaseUrl}${SELF_REGISTRATION_PATH}`, registrationPayload, {
      headers: getRegistrationHeaders(registration.data, consumerApp),
    });
  },
);
